import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import type { GeoJsonFeatureCollection } from '@gpx-spa/geo-domain';
import { CORRIDOR_BUFFER_MEMORY_CACHE_MAX_ENTRIES } from '@/map/operational/corridor-buffer-cache-constants';
import type { CorridorBufferPersistAdapter } from '@/map/operational/corridor-buffer-persist-types';

type MemoryEntry = {
  featureCollection: GeoJsonFeatureCollection;
  expiresAt: number;
};

function memoryKey(shipmentId: number, inputHash: string): string {
  return `${shipmentId}:${inputHash}`;
}

export const useCorridorBufferCacheStore = defineStore(
  'corridorBufferCache',
  () => {
    const persistAdapter = shallowRef<CorridorBufferPersistAdapter | null>(
      null,
    );
    const memory = ref(new Map<string, MemoryEntry>());

    function setPersistAdapter(adapter: CorridorBufferPersistAdapter | null) {
      persistAdapter.value = adapter;
      if (adapter && typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => {
          void adapter.clearExpired().catch(() => {});
        });
      } else if (adapter) {
        void adapter.clearExpired().catch(() => {});
      }
    }

    function evictLruIfNeeded(): void {
      const max = CORRIDOR_BUFFER_MEMORY_CACHE_MAX_ENTRIES;
      const m = memory.value;
      while (m.size > max) {
        const first = m.keys().next().value as string | undefined;
        if (first === undefined) break;
        m.delete(first);
      }
    }

    function memoryTouch(key: string, entry: MemoryEntry): void {
      const m = memory.value;
      m.delete(key);
      m.set(key, entry);
    }

    async function getCachedFeatureCollection(
      shipmentId: number,
      inputHash: string,
    ): Promise<GeoJsonFeatureCollection | null> {
      const key = memoryKey(shipmentId, inputHash);
      const m = memory.value;
      const mem = m.get(key);
      if (mem) {
        if (mem.expiresAt < Date.now()) {
          m.delete(key);
        } else {
          memoryTouch(key, mem);
          return mem.featureCollection;
        }
      }

      const adapter = persistAdapter.value;
      if (!adapter) return null;

      const disk = await adapter.get(shipmentId, inputHash);
      if (!disk || disk.expiresAt < Date.now()) return null;

      memoryTouch(key, {
        featureCollection: disk.featureCollection,
        expiresAt: disk.expiresAt,
      });
      evictLruIfNeeded();
      return disk.featureCollection;
    }

    async function setCachedFeatureCollection(
      shipmentId: number,
      inputHash: string,
      featureCollection: GeoJsonFeatureCollection,
      meta: { algorithmVersion: number; expiresAt: number },
    ): Promise<void> {
      if (featureCollection.features.length === 0) {
        return;
      }

      const key = memoryKey(shipmentId, inputHash);
      const entry: MemoryEntry = {
        featureCollection,
        expiresAt: meta.expiresAt,
      };
      memoryTouch(key, entry);
      evictLruIfNeeded();

      const adapter = persistAdapter.value;
      if (adapter) {
        void adapter
          .set(shipmentId, {
            inputHash,
            algorithmVersion: meta.algorithmVersion,
            expiresAt: meta.expiresAt,
            featureCollection,
          })
          .catch(() => {});
      }
    }

    return {
      persistAdapter,
      setPersistAdapter,
      getCachedFeatureCollection,
      setCachedFeatureCollection,
    };
  },
);
